/**
 * 4-step launch — vertical onboarding timeline with drawing line + numbered pills.
 * The last step pulses to signal "go".
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
} from "../../reel/sceneShared.js";
import { splitItem, sceneHeader } from "./_mockHelpers.js";

export const fourStepLaunch: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const headline = inputs.text ?? "";
  const eyebrow = inputs.subtext ?? "";
  const items = (inputs.items?.length ? inputs.items : ["1|Anmelden", "2|Profil", "3|Veröffentlichen", "4|Verkaufen"]).slice(0, 4);

  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const lineId = `${clipPrefix}-rail`;
  const dotIds = items.map((_, i) => `${clipPrefix}-dot-${i}`);
  const cardIds = items.map((_, i) => `${clipPrefix}-card-${i}`);

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const header = sceneHeader({ clipPrefix, accent, eyebrow, title: headline, titlePx: lg * 0.78, subPx: sm * 0.95 });

  const stepsHtml = items.map((raw, i) => {
    const [title, desc] = splitItem(raw);
    const isLast = i === items.length - 1;
    return `<div style="display:flex;align-items:flex-start;gap:24px;position:relative;">
      <div id="${dotIds[i]}" style="position:relative;width:64px;height:64px;border-radius:9999px;background:${isLast ? accent : withAlpha(accent, 0.18)};border:2px solid ${withAlpha(accent, 0.55)};color:${isLast ? themeColors.bg : accent};display:flex;align-items:center;justify-content:center;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${md * 0.55}px;flex-shrink:0;z-index:2;box-shadow:${isLast ? `0 0 32px ${withAlpha(accent, 0.65)}` : `0 0 16px ${withAlpha(accent, 0.25)}`};">${i + 1}</div>
      <div id="${cardIds[i]}" style="flex:1;background:linear-gradient(165deg, ${withAlpha(themeColors.fg, 0.07)} 0%, ${withAlpha(themeColors.fg, 0.02)} 100%);border:1.5px solid ${withAlpha(themeColors.fg, 0.12)};border-radius:24px;padding:20px 26px;display:flex;flex-direction:column;gap:6px;box-shadow:0 18px 40px rgba(0,0,0,0.35);">
        <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${md * 0.58}px;letter-spacing:-0.8px;color:${themeColors.fg};line-height:1.05;">${escapeHtml(title ?? "")}</div>
        ${desc ? `<div style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${sm * 0.78}px;color:${withAlpha(themeColors.fg, 0.78)};line-height:1.35;">${escapeHtml(desc)}</div>` : ""}
      </div>
    </div>`;
  }).join("");

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:36px;padding:72px;">
    ${header.html}
    <div style="position:relative;display:flex;flex-direction:column;gap:20px;width:100%;max-width:820px;">
      <div style="position:absolute;left:32px;top:32px;bottom:32px;width:2px;background:${withAlpha(themeColors.fg, 0.1)};border-radius:2px;">
        <div id="${lineId}" style="position:absolute;left:0;top:0;width:100%;height:0%;background:linear-gradient(180deg, ${accent} 0%, ${withAlpha(accent, 0.3)} 100%);box-shadow:0 0 18px ${withAlpha(accent, 0.55)};"></div>
      </div>
      ${stepsHtml}
    </div>
  </div>`;

  const init = [
    eyebrow ? gsapSetLine(`#${header.ids.eyebrow}`, { opacity: 0, y: 12 }) : "",
    headline ? gsapSetLine(`#${header.ids.title}`, { opacity: 0, y: 16 }) : "",
    gsapSetLine(`#${lineId}`, { height: "0%" }),
    ...dotIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, scale: 0.5 })),
    ...cardIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, x: 30 })),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [];
  if (eyebrow) tweens.push(t.to(`#${header.ids.eyebrow}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 0.0));
  if (headline) tweens.push(t.to(`#${header.ids.title}`, { opacity: 1, y: 0, duration: 0.6, ease: "raw:\"power2.out\"" }, 0.15));
  const lineDur = Math.max(1.2, clipDurationSec * 0.55);
  tweens.push(t.to(`#${lineId}`, { height: "100%", duration: lineDur, ease: "raw:\"power2.inOut\"" }, 0.4));
  const stagger = Math.max(0.35, lineDur / Math.max(items.length, 1));
  dotIds.forEach((id, i) => tweens.push(t.to(`#${id}`, { opacity: 1, scale: 1, duration: 0.45, ease: "raw:\"back.out(2)\"" }, 0.45 + i * stagger)));
  cardIds.forEach((id, i) => tweens.push(t.to(`#${id}`, { opacity: 1, x: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.55 + i * stagger)));
  if (dotIds.length) tweens.push(t.to(`#${dotIds[dotIds.length - 1]}`, { scale: 1.15, duration: 0.6, repeat: -1, yoyo: true, ease: "raw:\"sine.inOut\"" }, 0.45 + items.length * stagger));
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
